import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { fetchHelpVideos } from "@/api/help";
import { HelpArticleCard } from "@/components/help/HelpArticleCard";
import { useDelayedFlag } from "@/components/help/useDelayedFlag";
import { GfgButton } from "@/components/ui/GfgButton";
import { HELP_WATCH } from "@/constants/help";
import { apiErrorMessage } from "@/utils/apiError-helper";

const SKELETON_KEYS = ["skeleton-1", "skeleton-2", "skeleton-3"];

export interface HelpWatchViewProps {
  /** Focus this view's heading on show — set when it was reached from inside the panel. */
  focusOnShow: boolean;
  onSelectArticle: (slug: string) => void;
}

/** The panel rail's Watch listing: every published article that carries a walkthrough video. */
export function HelpWatchView({ focusOnShow, onSelectArticle }: HelpWatchViewProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const {
    data: articles,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["help", "videos"],
    queryFn: fetchHelpVideos,
  });

  const showSkeleton = useDelayedFlag(isLoading);

  useEffect(() => {
    if (focusOnShow) headingRef.current?.focus();
  }, [focusOnShow]);

  useEffect(() => {
    rootRef.current?.closest<HTMLElement>("[data-help-pane]")?.scrollTo({ top: 0 });
  }, []);

  return (
    <div ref={rootRef} className="mx-auto w-full max-w-[560px] max-[900px]:max-w-none">
      <h2
        ref={headingRef}
        tabIndex={-1}
        className="m-0 font-crimson font-normal text-[24px] text-rodeo-black outline-none"
      >
        {HELP_WATCH.LIST_HEADING}
      </h2>
      {!isLoading && !isError && !!articles && (
        <p className="mt-[5px] max-w-[60ch] text-[13px] text-graphite">
          {articles.length}{" "}
          {articles.length === 1 ? HELP_WATCH.WALKTHROUGH_SINGULAR : HELP_WATCH.WALKTHROUGHS}
        </p>
      )}

      <div className="mt-5 flex flex-col">
        {showSkeleton && (
          <div className="flex flex-col gap-3">
            {SKELETON_KEYS.map((key) => (
              <div
                key={key}
                className="h-16 animate-pulse rounded-card bg-wax"
                aria-hidden="true"
              />
            ))}
          </div>
        )}

        {isError && (
          <div className="flex flex-col items-center gap-3 rounded-card border border-card-border py-10 text-center">
            <p className="text-[14px] text-graphite">
              {apiErrorMessage(error, "We couldn't load the walkthroughs. Please try again.")}
            </p>
            <GfgButton type="button" variant="outline" onClick={() => refetch()}>
              Retry
            </GfgButton>
          </div>
        )}

        {!isLoading && !isError && articles?.length === 0 && (
          <div className="rounded-card border border-card-border py-10 text-center">
            <p className="text-[14px] text-graphite">No walkthroughs yet.</p>
          </div>
        )}

        {!isLoading &&
          !isError &&
          !!articles?.length &&
          articles.map((article) => (
            <HelpArticleCard key={article.slug} article={article} onSelect={onSelectArticle} />
          ))}
      </div>
    </div>
  );
}

export default HelpWatchView;
